import { Controller } from "@hotwired/stimulus"

// Pre-live device checklist — mirrors hifi-live-host.jsx `HostPreLiveHi`
// (lignes 7-83). Drives 4 row states:
//   • Caméra      → live preview + label + resolution
//   • Micro       → live RMS level (dB) + label
//   • Connexion   → round-trip latency to the Rails app (proxy for network)
//   • Sondages    → static informational row (data passed from the server)
//
// Each row carries 3 sub-targets: an icon (pending/ok/warn), a primary
// label and a secondary metadata line. The "Ouvrir la salle d'animation"
// button stays disabled until camera + mic are green.

const PENDING_CLASS = "is-pending"
const OK_CLASS      = "is-ok"
const WARN_CLASS    = "is-warn"

export default class extends Controller {
  static targets = [
    "video",
    "videoEmpty",
    "micMeter",
    "micBar",
    "camRow",  "camLabel",  "camMeta",
    "micRow",  "micLabel",  "micMeta",
    "netRow",  "netLabel",  "netMeta",
    "submit",
    "permissionError"
  ]

  async connect() {
    this._cameraReady = false
    this._micReady    = false
    this._refreshSubmit()

    try {
      this._stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      })
    } catch (e) {
      this._setRowState(this.camRowTarget, WARN_CLASS, "Caméra · accès refusé", "Autorisez l'accès puis rechargez")
      this._setRowState(this.micRowTarget, WARN_CLASS, "Micro · accès refusé", "Autorisez l'accès puis rechargez")
      if (this.hasPermissionErrorTarget) this.permissionErrorTarget.hidden = false
      return
    }

    this._setupVideo()
    this._setupAudioMeter()
    this._startNetCheck()
  }

  disconnect() {
    if (this._raf) cancelAnimationFrame(this._raf)
    if (this._netInterval) clearInterval(this._netInterval)
    if (this._audioCtx) { try { this._audioCtx.close() } catch (_) {} }
    if (this._stream) this._stream.getTracks().forEach(t => t.stop())
  }

  _setupVideo() {
    if (!this.hasVideoTarget) return
    const v = this.videoTarget
    v.srcObject = this._stream
    v.muted = true
    v.playsInline = true
    v.play().catch(() => {})

    if (this.hasVideoEmptyTarget) this.videoEmptyTarget.hidden = true

    const track = this._stream.getVideoTracks()[0]
    if (track) {
      const settings = track.getSettings()
      const res = (settings.height && settings.width)
                    ? `${settings.height}p`
                    : "résolution OK"
      this._setRowState(this.camRowTarget, OK_CLASS, `Caméra · ${res}`, track.label || "Caméra activée")
      this._cameraReady = true
      this._refreshSubmit()
    } else {
      this._setRowState(this.camRowTarget, WARN_CLASS, "Caméra · aucune source", "Branchez ou autorisez une caméra")
    }
  }

  _setupAudioMeter() {
    const audioTrack = this._stream.getAudioTracks()[0]
    if (!audioTrack) {
      this._setRowState(this.micRowTarget, WARN_CLASS, "Micro · aucune source", "Branchez ou autorisez un micro")
      return
    }

    // Most browsers expose the device label via the track. AirPods → "AirPods Pro".
    const micLabel = audioTrack.label || "Micro activé"

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      this._audioCtx = new AudioCtx()
      const source   = this._audioCtx.createMediaStreamSource(this._stream)
      const analyser = this._audioCtx.createAnalyser()
      analyser.fftSize = 1024
      source.connect(analyser)
      const data = new Uint8Array(analyser.frequencyBinCount)

      let peakSeen = 0
      const tick = () => {
        analyser.getByteFrequencyData(data)
        // RMS over the band, normalised 0-1
        let sum = 0
        for (let i = 0; i < data.length; i++) sum += data[i] * data[i]
        const rms = Math.sqrt(sum / data.length) / 255
        const bars = this.hasMicBarTarget ? this.micBarTargets : []
        bars.forEach((bar, i) => {
          const threshold = (i + 1) / bars.length
          bar.classList.toggle("on", rms >= threshold * 0.5)
        })
        peakSeen = Math.max(peakSeen, rms)

        // Once we've seen the user's voice at least once (>3% RMS), mark
        // the row green. Updates the metadata every frame with the live dB.
        if (peakSeen > 0.03) {
          const db = Math.round(20 * Math.log10(Math.max(rms, 0.001)))
          this._setRowState(this.micRowTarget, OK_CLASS, "Micro · niveau OK", `${micLabel} · ${db} dB`)
          this._micReady = true
          this._refreshSubmit()
        } else {
          this._setRowState(this.micRowTarget, PENDING_CLASS, "Micro · parlez pour tester", micLabel)
        }
        this._raf = requestAnimationFrame(tick)
      }
      tick()
    } catch (e) {
      // Audio context refused — fall back to "OK" based on track presence.
      this._setRowState(this.micRowTarget, OK_CLASS, "Micro · activé", micLabel)
      this._micReady = true
      this._refreshSubmit()
    }
  }

  _startNetCheck() {
    // Latency-only proxy: fetch a tiny known endpoint and measure RTT.
    // We use `/up` (Rails Solid Queue health-style endpoint) if present,
    // else fall back to the current page HEAD.
    const probeUrl = "/up"
    const sample = async () => {
      const t0 = performance.now()
      try {
        await fetch(probeUrl, { method: "HEAD", cache: "no-store" })
        const rtt = Math.round(performance.now() - t0)
        let label, meta, state
        if (rtt < 80) {
          state = OK_CLASS
          label = "Connexion · stable"
          meta  = `Latence ${rtt} ms`
        } else if (rtt < 250) {
          state = OK_CLASS
          label = "Connexion · correcte"
          meta  = `Latence ${rtt} ms`
        } else {
          state = WARN_CLASS
          label = "Connexion · lente"
          meta  = `Latence ${rtt} ms — risque de coupures`
        }
        this._setRowState(this.netRowTarget, state, label, meta)
      } catch (_) {
        this._setRowState(this.netRowTarget, WARN_CLASS, "Connexion · échec", "Impossible de joindre le serveur")
      }
    }
    sample()
    this._netInterval = setInterval(sample, 8000)
  }

  _setRowState(row, state, label, meta) {
    if (!row) return
    row.classList.remove(PENDING_CLASS, OK_CLASS, WARN_CLASS)
    row.classList.add(state)
    const labelEl = row.querySelector("[data-preflight-target$='Label']")
    const metaEl  = row.querySelector("[data-preflight-target$='Meta']")
    if (labelEl && label !== undefined) labelEl.textContent = label
    if (metaEl  && meta  !== undefined) metaEl.textContent  = meta
  }

  _refreshSubmit() {
    if (!this.hasSubmitTarget) return
    this.submitTarget.disabled = !(this._cameraReady && this._micReady)
  }
}
